import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { getStats, getTimeSeriesStats, getStorageStats } from 'src/api/functions/statsApi';
import { retryOnTransientStatus } from 'src/api/utils/helpers';
import { useAiCreditsQuery, useAiBudgetQuery } from 'src/api/queries/aiSettingsQueries';
import { useCollectionCountQuery } from 'src/api/queries/collectionsQueries';
import { useShareCountQuery } from 'src/api/queries/shareQueries';
import { useUserManagementQuery } from 'src/api/queries/userManagementQueries';
import { useAccessToken } from 'src/hooks/useAccessToken';
import {
  ApiStatsUsageTypes,
  CdnStatsUsageTypes,
  TranscodeStatsUsageTypes,
  userTypes,
  AddonStatsUsageTypes,
  LbStatsUsageTypes,
  StorageStatsUsageTypes,
} from 'src/types/TCheckUsage';
import styles from './CheckUsage.module.scss';
import { Button, ChartComponent, Input, Pager, PageTitle, StatsCountShimmer } from 'src/components';
import CheckUsageDropDown from './DropDown/DropDown';
import { Link, useLocation } from 'react-router-dom';
import { convertSeconds, formatBillingBytes, formatNumberIntl, maxDate, minDate } from 'src/utils/common';
import { getSearch } from 'src/api/functions/assetsApi';
import Stats_API from 'src/assets/icons/Stats_API.svg';
import Stats_Assets from 'src/assets/icons/Stats_Assets.svg';
import Stats_CDN from 'src/assets/icons/Stats_CDN.svg';
import Stats_Addons from 'src/assets/icons/Stats_Addons.svg';
import Stats_User from 'src/assets/icons/Stats_User.svg';
import Stats_Duration from 'src/assets/icons/Stats_Duration.svg';
import Stats_LB from 'src/assets/icons/Stats_Load_balancer.svg';
import Stats_Storage from 'src/assets/icons/Stats_Storage.svg';
import Stats_Collections from 'src/assets/icons/Stack.svg';
import Stats_Shares from 'src/assets/icons/ShareNetwork.svg';
import { cdnSubTypes, apiSubTypes, storageSubTypes } from '../Asset/helpers/usageDetailsSubTypes';
import { addonsSettingsAtom } from 'src/shared/settings/addons';
import { useAtom } from 'jotai';
import { notAllowedCDNs } from 'src/utils/cdns';
import { useAlerts } from 'src/common/AlertManager';
import { cdnSettingsAtom } from 'src/shared/settings/cdn';
import BoardingTour from 'src/components/BoardingTour';
import GettingStartedChecklist from 'src/components/GettingStartedChecklist';
import { config } from 'src/utils/config';
import TeleportStats from './TeleportStats';


type ErrorProps = {
  message: string;
};

const LoadingStatus: React.FC<{ val: number }> = ({ val }) => {
  return (
    <div className={styles.gridContainer}>
      {new Array(val).fill(0).map((_, i) => (
        <StatsCountShimmer key={i} />
      ))}
    </div>
  );
};

const ErrorStatus = ({ message }: ErrorProps) => {
  return (
    <div className={styles.loadingHolder}>
      <h3 style={{ fontSize: 'var(--font-size-xl)' }}>{message}</h3>
    </div>
  );
};

// Shared with GettingStartedChecklist (same react-query key) so /api/v1/users
// is fetched once per page load instead of twice in parallel (which used to
// race the rate limiter on /usage).
const USER_LIST_FILTERS = { page: 1 } as const;

const periodMap: Record<string, string> = {
  '24': 'Past 24 Hours',
  '168': 'Past 1 Week',
  '730': 'Past 1 Month',
  '8760': 'Past 1 Year',
};
const queryTimeRange = 'Past 24 Hours';

type TabType = 'cloud' | 'teleport';

export const CheckUsageComponent: React.FC<{ assetNavBarRef?: any }> = ({ assetNavBarRef }) => {
  const accessToken = useAccessToken();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<TabType>('cloud');
  const [apiStatData, setApiStatData] = useState<ApiStatsUsageTypes>();
  const [cdnStatsData, setCdnStatsData] = useState<CdnStatsUsageTypes>();
  const [transcodeStatData, setTranscodeStatsData] = useState<TranscodeStatsUsageTypes>();
  const [addonStatData, setAddonStatData] = useState<AddonStatsUsageTypes>();
  const [lbStatsData, setLbStatsData] = useState<LbStatsUsageTypes>();
  const [storageData, setStorageData] = useState<StorageStatsUsageTypes>();
  const [selectedUser, setSelectedUser] = useState('all');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [statsLoading, setStatsLoading] = useState<boolean>(false);
  const [isAssetValuesLoading, setIsAssetValuesLoading] = useState<boolean>(false);
  const [totalAssetValue, setTotalAssetValue] = useState<number>(0);
  const [apiTopValues, setApiTopValues] = useState<{ timestamp: string; value: number }[]>([]);
  const [addonsTopValues, setAddonsTopValue] = useState<{ timestamp: string; value: number }[]>([]);
  const [addonsSettings] = useAtom(addonsSettingsAtom);
  const [hasFetched, setHasFetched] = useState<boolean>(false);
  const [selectedTimeRange, setSelectedTimeRange] = useState<string>(queryTimeRange);
  const [isCustomRangeApplied, setIsCustomRangeApplied] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);

  const [cdnSettings] = useAtom(cdnSettingsAtom);

  const { showAlert } = useAlerts();
  const dateRef = useRef<HTMLDivElement>(null);

  const addonsSubTypes = useMemo(() => {
    const settings = addonsSettings ?? {};
    return Object.entries(settings)
      .filter(([key]) => key !== 'AI_ASSISTANT')
      .map(([key]) => ({ key }));
  }, [addonsSettings]);

  const timeOptions: { key: string; value: JSX.Element }[] = [
    'Past 24 Hours',
    'Past 1 Week',
    'Past 1 Month',
    'Past 1 Year',
    'Custom',
  ].map((key) => ({
    key,
    value: <span>{key}</span>,
  }));
  const handleStartDateChange = (value: string) => {
    setStartDate(value || '');
  };

  const handleEndDateChange = (value: string) => {
    setEndDate(value || '');
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dateRef.current && !dateRef.current.contains(e.target as Node)) {
        setIsDateRangeOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleSubmitDate = () => {
    if (!startDate) {
      showAlert('Please select a start date');
      return;
    }

    if (!endDate) {
      showAlert('Please select an end date');
      return;
    }

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (start > end) {
      showAlert('Start date cannot be after end date');
      return;
    }

    // Apply custom date range
    setIsCustomRangeApplied(true);
    setIsDateRangeOpen(false);
    // fetchStats will be called automatically via useEffect dependency
  };

  // Handle clearing/canceling date selection
  const handleClearDate = () => {
    if (!startDate && !endDate) {
      setIsDateRangeOpen(false);
      return;
    }

    setStartDate('');
    setEndDate('');

    if (isCustomRangeApplied) {
      setSelectedTimeRange(queryTimeRange);
      setIsCustomRangeApplied(false);
    }
    setIsDateRangeOpen(false);
  };

  const fetchStats = useCallback(async () => {
    if (selectedTimeRange === 'Custom' && !isCustomRangeApplied) {
      return;
    }

    setStatsLoading(true);
    setHasFetched(false);

    try {
      const useStartDate = isCustomRangeApplied ? startDate : undefined;
      const useEndDate = isCustomRangeApplied ? endDate : undefined;
      const useTimeRange = isCustomRangeApplied ? 'Custom' : selectedTimeRange;

      // Wrap each call with retry-on-429/5xx. The first paint of /usage
      // fires six /stats/* requests in parallel and the backend rate
      // limiter occasionally rejects one (most often /transcodes), which
      // would otherwise leave its stat card stuck on the loading shimmer.
      const apiCalls = [
        retryOnTransientStatus(() =>
          getStats(accessToken, useStartDate, useEndDate, selectedUserId, 'api', useTimeRange, ''),
        ),
        retryOnTransientStatus(() =>
          getStats(
            accessToken,
            useStartDate,
            useEndDate,
            selectedUserId,
            'cdn',
            useTimeRange,
            cdnSettings?.image_cdn ?? '',
          ),
        ),
        retryOnTransientStatus(() =>
          getStats(accessToken, useStartDate, useEndDate, selectedUserId, 'transcodes', useTimeRange, ''),
        ),
        retryOnTransientStatus(() =>
          getStats(accessToken, useStartDate, useEndDate, selectedUserId, 'addons', useTimeRange, ''),
        ),
      ];

      // Only fetch LB stats if enabled
      if (config.showDataTransferredStats) {
        apiCalls.push(
          retryOnTransientStatus(() =>
            getStats(accessToken, useStartDate, useEndDate, selectedUserId, 'lb', useTimeRange, ''),
          ),
        );
      }

      // Only fetch storage stats if enabled
      if (config.showStorageStats) {
        apiCalls.push(retryOnTransientStatus(() => getStorageStats(accessToken, selectedUserId)));
      }

      const responses = await Promise.all(apiCalls);
      let responseIndex = 0;

      setApiStatData(responses[responseIndex++] as ApiStatsUsageTypes);
      setCdnStatsData(responses[responseIndex++] as CdnStatsUsageTypes);
      setTranscodeStatsData(responses[responseIndex++] as TranscodeStatsUsageTypes);
      setAddonStatData(responses[responseIndex++] as AddonStatsUsageTypes);
      
      // Only set LB data if enabled
      if (config.showDataTransferredStats) {
        setLbStatsData(responses[responseIndex++] as LbStatsUsageTypes);
      } else {
        setLbStatsData(undefined);
      }
      
      // Only set storage data if enabled
      if (config.showStorageStats) {
        setStorageData(responses[responseIndex++] as StorageStatsUsageTypes);
      } else {
        setStorageData(undefined);
      }
    } catch (error) {      
      showAlert('Error fetching stats');
    } finally {
      setStatsLoading(false);
      setHasFetched(true);
    }
  // `addonsSettings` deliberately not in deps: it hydrates asynchronously
  // from /api/v1/user/settings (often after a rate-limited retry), and
  // including it would refire every stats endpoint a second time. The AI
  // credits fetch — the only piece that actually needs it — lives in its
  // own effect below. cdnSettings is read at call time on purpose so a
  // late hydration doesn't refire either; the CDN stats endpoint accepts
  // an empty `cdn_name` and refines once filters/range actually change.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, selectedUserId, startDate, endDate, selectedTimeRange, isCustomRangeApplied]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // AI credits live on a different endpoint and depend on whether the AI
  // addon is enabled. Using the shared react-query hook here (instead of
  // a hand-rolled fetch) gives us the hook's built-in retry-on-429 policy,
  // which matters because this request fires the instant `addonsSettings`
  // hydrates and tends to collide with `BudgetIndicator`'s budget query
  // at exactly the moment the /stats/* rate-limit burst is exhausted.
  const hasAiAddon = Boolean(
    addonsSettings?.AI_ASSISTANT?.added && addonsSettings?.AI_ASSISTANT?.enabled,
  );
  const aiPeriod = useMemo(() => {
    const useTimeRange = isCustomRangeApplied ? 'Custom' : selectedTimeRange;
    if (useTimeRange === 'Past 1 Week') return '168';
    if (useTimeRange === 'Past 1 Month') return '720';
    // Backend caps period_hours at 8760 (one year). Previously this
    // returned '720', silently showing 30 days of usage when the user
    // asked for a year — confusing when the same number was compared
    // against the /insights/ai-usage breakdown's own 30-day window.
    if (useTimeRange === 'Past 1 Year') return '8760';
    return '24';
  }, [selectedTimeRange, isCustomRangeApplied]);
  const aiCreditsEnabled =
    hasAiAddon && !(selectedTimeRange === 'Custom' && !isCustomRangeApplied);
  const { data: aiStatData } = useAiCreditsQuery(aiPeriod, {
    enabled: aiCreditsEnabled,
  });

  // Phase 3: surface the live AI credit balance on the usage overview.
  // Re-uses the existing budget endpoint (also driving the chat
  // CreditRing) so we don't issue a second request to /stats/ai/ledger
  // just for one number.
  const { data: aiBudgetData } = useAiBudgetQuery({
    enabled: hasAiAddon,
  });

  // Counts for the Collections / Shares overview tiles. The
  // count-only hooks hit `?count_only=y` so the backend skips
  // hydrating rows — we just need the totals here. They live on
  // their own query keys so they don't collide with the full lists
  // cached by /collections and /shares.
  const { data: collectionsCount, isError: collectionsCountError } = useCollectionCountQuery();
  const { data: sharesCount, isError: sharesCountError } = useShareCountQuery();
  const totalCollections = collectionsCount?.data?.total_collections ?? null;
  const totalShares = sharesCount?.data?.shares_found ?? null;

  // Shared cache with GettingStartedChecklist on the same page — using the
  // same query key dedupes the simultaneous /api/v1/users requests that
  // used to race the rate limiter (one returning 200, one 429).
  const {
    data: userData,
    isLoading: userDataLoading,
    isError: userDataError,
  } = useUserManagementQuery(USER_LIST_FILTERS) as {
    data: userTypes | undefined;
    isLoading: boolean;
    isError: boolean;
  };

  useEffect(() => {
    if (userDataError) {
      showAlert('Error fetching user data');
    }
  }, [userDataError, showAlert]);

  useEffect(() => {
    const fetchAssetCount = async () => {
      try {
        setIsAssetValuesLoading(true);

        // Single search with limit_per_page: 1 — we only need `total_files`
        // for the overview tile. Previously this fired three parallel
        // searches (all / image / video) just to label the card; that
        // breakdown was dropped in favour of one number to keep the /usage
        // page from hammering /search on first paint.
        const totalAssetsResponse = await getSearch(
          {
            page: 1,
            hash: '',
            face: null,
            limit_per_page: 1,
            keyword: '',
            extended_result: false,
            sort_by: ['upload_time_range_DESC'],
            file_name: '',
            upload_time_range: {},
            status: '',
            custom_content_input: '',
            filters: null,
            content_type: '',
            trashed: false,
          },
          accessToken,
        );

        setTotalAssetValue(totalAssetsResponse?.total_files || 0);
      } catch (error) {
        showAlert('Error fetching Asset Data');
      } finally {
        setIsAssetValuesLoading(false);
      }
    };

    fetchAssetCount();
  }, []);

  // API top values processing
  useEffect(() => {
    if (apiStatData && apiStatData.data) {
      const dynamicKeys = Object.keys(apiStatData.data);

      if (dynamicKeys.length > 0) {
        const firstKey = dynamicKeys[0];
        const routeMap = apiStatData.data[firstKey];
        const entries = Object.entries(routeMap);
        if (entries.length > 0) {
          const top10 = entries
            .sort((a, b) => Number(b[1]) - Number(a[1]))
            .slice(0, 10)
            .map(([key, value]) => ({
              timestamp: key,
              value: Number(value),
            }));
          setApiTopValues(top10);
        } else {
          setApiTopValues([]);
        }
      } else {
        setApiTopValues([]);
      }
    } else {
      setApiTopValues([]);
    }
  }, [apiStatData]);

  // Addons top values processing
  useEffect(() => {
    if (addonStatData && addonStatData.data) {
      const dynamicKeys = Object.keys(addonStatData?.data);

      if (dynamicKeys.length > 0) {
        const firstKey = dynamicKeys[0];
        const routeMap = addonStatData.data[firstKey];
        const entries = Object.entries(routeMap).filter(([key]) => key !== 'AI_ASSISTANT');
        if (entries.length > 0) {
          const top10 = entries
            .sort((a, b) => Number(b[1]) - Number(a[1]))
            .map(([key, value]) => ({
              timestamp: String(key),
              value: Number(value),
            }));
          setAddonsTopValue(top10);
        } else {
          setAddonsTopValue([]);
        }
      } else {
        setAddonsTopValue([]);
      }
    } else {
      setAddonsTopValue([]);
    }
  }, [addonStatData]);

  const totalCountForAPI = useMemo(() => {
    if (!apiStatData?.data) return null;
    let count = 0;
    Object.values(apiStatData.data).forEach((group) => {
      Object.values(group).forEach((value) => {
        count += parseInt(value);
      });
    });
    return count;
  }, [apiStatData]);

  const totalCountForAddons = useMemo(() => {
    if (!addonStatData?.data) return null;
    let count = 0;
    Object.values(addonStatData.data).forEach((group) => {
      Object.entries(group).forEach(([key, value]) => {
        if (key !== 'AI_ASSISTANT') {
          count += parseInt(value);
        }
      });
    });
    return count;
  }, [addonStatData]);

  const totalCountForTranscodes = useMemo(() => {
    if (!transcodeStatData?.data) return null;
    let count = 0;
    Object.values(transcodeStatData.data).forEach((group) => {
      Object.values(group).forEach((value) => {
        count += parseInt(value);
      });
    });
    return count;
  }, [transcodeStatData]);

  const totalCDNUsage = useMemo(() => {
    if (!cdnStatsData?.data) return null;
    return cdnStatsData.data.sum || 0;
  }, [cdnStatsData]);

  const totalLBUsage = useMemo(() => {
    if (!lbStatsData?.data) return null;
    return lbStatsData.data.sum || 0;
  }, [lbStatsData]);

  const totalStorageUsage = useMemo(() => {
    if (!storageData?.data) return null;
    
    // Extract value from the new API response format: data["*"]["ASSET"]
    return storageData.data?.["*"]?.["ASSET"] || 0;
  }, [storageData]);

  const aiCreditsUsed = useMemo(() => {
    if (!aiStatData) return null;
    return aiStatData.credits_used ?? 0;
  }, [aiStatData]);

  const isLoading = statsLoading;
  // Per-subscription figures (Cloudflare/ClickHouse, alone or summed with the
  // account's dedicated CDN77 resources — source 'both') are attributable to
  // this account even on shared hostnames like cdn.filespin.io, so the card
  // is shown; only the legacy CDN77-only path hides shared hostnames
  // (notAllowedCDNs).
  const perSubscriptionCdn =
    cdnStatsData?.source === 'cloudflare' || cdnStatsData?.source === 'both';
  const isAllowedCDN =
    perSubscriptionCdn || !notAllowedCDNs.includes(cdnStatsData?.cdn ?? '');
  const cdnCardDescription = perSubscriptionCdn
    ? cdnStatsData?.live === false
      ? 'Delivered via the CDN (estimate) — data through yesterday'
      : 'Delivered via the CDN (estimate)'
    : 'Data served via the CDN';
  const renderStatCard = (
    title: string,
    description: string,
    value: any,
    icon?: string,
    linkText?: string,
    linkUrl?: any,
    formatFn?: (v: number) => string,
    isBackground?: boolean,
    type?: string,
    statsData?: any,
    graphTitle?: string,
    graphDesc?: string,
  ) => (
    <div className={styles.TotalCountCards}>
      <div className={styles.CardsRow}>
        <div className={styles.textContainer}>
          <p className={styles.statCardTitle}>{title}</p>
          <p className={styles.statCardDescription}>{description}</p>
          <div className={styles.statCardValue}>
            {value === null ? <StatsCountShimmer /> : formatFn ? formatFn(value) : value}
          </div>
        </div>
        {icon && (
          <div className={styles.iconContainer}>
            <img src={icon} alt="Stats Icon" width={50} height={50} />
          </div>
        )}
      </div>
      {linkText && linkUrl && (
        <div className={styles.linkContainer}>
          {isBackground ? (
            <Link
              className={styles.cardLink}
              to={{
                pathname: linkUrl,
                state: {
                  background: location,
                  data: {
                    type: type,
                    userData: userData?.total_users !== 0 ? userData?.users : [],
                    statsData: statsData,
                    accessToken: accessToken,
                    graphTitle: graphTitle,
                    graphDesc: graphDesc,
                    icon: icon,
                  },
                },
              }}
            >
              {linkText}
            </Link>
          ) : (
            <Link className={styles.cardLink} to={linkUrl}>
              {linkText}
            </Link>
          )}
        </div>
      )}
    </div>
  );

  return (
    <>
      <BoardingTour
        title="Account Usage"
        description="Get a comprehensive summary of your account’s total assets, users, and usage statistics."
        path="/usage"
        uniqueId="USAGE"
      />
      <GettingStartedChecklist />
      <div className={styles.mainContainer} data-test="check-usage-page-content">
        <PageTitle title="Account Usage" />

        {/* Account overview — always visible, regardless of which tab is active.
            These are account-wide totals, not cloud-specific stats. */}
        {userDataLoading || isAssetValuesLoading ? (
          <LoadingStatus val={5} />
        ) : (
          <div className={styles.gridContainer}>
            {renderStatCard(
              'Users',
              'Number of Users',
              userData?.total_users || 0,
              Stats_User,
              'View',
              `/admin/users`,
              undefined,
              false,
            )}
            {renderStatCard(
              'Assets',
              'Number of Assets',
              formatNumberIntl(totalAssetValue),
              Stats_Assets,
              'View',
              '/assets',
              undefined,
              false,
            )}
            {renderStatCard(
              'Collections',
              'Number of Collections',
              totalCollections === null
                ? collectionsCountError
                  ? '—'
                  : null
                : formatNumberIntl(totalCollections),
              Stats_Collections,
              'View',
              '/collections',
              undefined,
              false,
            )}
            {renderStatCard(
              'Shares',
              'Number of Shares',
              totalShares === null ? (sharesCountError ? '—' : null) : formatNumberIntl(totalShares),
              Stats_Shares,
              'View',
              '/shares',
              undefined,
              false,
            )}
            {config.showStorageStats && renderStatCard(
              'Storage',
              'Total Storage used',
              totalStorageUsage,
              Stats_Storage,
              'Explore',
              `/usage/details/${selectedUserId}/${selectedUser}/${selectedTimeRange}`,
              formatBillingBytes,
              true,
              'storage',
              [],
              'Total Storage Volume',
              'Total storage space used across all assets',
            )}
          </div>
        )}

        {/* Tab strip — only rendered when there's actually more than one tab
            to switch between. When Teleport is gated off we drop straight
            into the Cloud body below. */}
        {config.showTeleportStats && (
          <div className={styles.tabContainer} style={{ marginTop: 32 }}>
            <button
              className={`${styles.tab} ${activeTab === 'cloud' ? styles.activeTab : ''}`}
              onClick={() => setActiveTab('cloud')}
            >
              Cloud
            </button>
            <button
              className={`${styles.tab} ${activeTab === 'teleport' ? styles.activeTab : ''}`}
              onClick={() => setActiveTab('teleport')}
            >
              Teleport
            </button>
          </div>
        )}

        {activeTab === 'teleport' && config.showTeleportStats ? (
          <TeleportStats />
        ) : (
          <>
            {/* Cloud tab body — usage filters, stat cards and top-N charts.
                Filters live inside the tab on purpose: Teleport has its own
                independent filtering and shouldn't share these controls. */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                borderBottom: '1px solid rgba(1, 10, 44, 0.07)',
                marginTop: config.showTeleportStats ? 0 : 32,
                paddingBottom: 12,
                position: 'relative',
              }}
            >
              {startDate && endDate && isCustomRangeApplied && (
                <span
                  style={{
                    marginRight: 'auto',
                    color: '#777',
                    fontSize: 'var(--font-size-s)',
                  }}
                >
                  {`(${new Date(startDate).toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                  })} - ${new Date(endDate).toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                  })})`}
                </span>
              )}
              <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                <CheckUsageDropDown
                  options={[
                    { key: 'all', value: <div style={{ display: 'flex', alignItems: 'center' }}>All Users</div> },
                    ...(userData?.users
                      ? userData.users.map((user: any) => ({
                          key: user.id,
                          value: (
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              <span style={{ marginRight: '10px' }}>{user.first_name}</span>
                              <span style={{ color: '#666' }}>{user.email}</span>
                            </div>
                          ),
                        }))
                      : []),
                  ]}
                  selectedKeys={[selectedUser]}
                  onClick={(key) => {
                    setSelectedUser(key);
                    setSelectedUserId(key === 'all' ? null : key);
                  }}
                  placeholder="All"
                >
                  {selectedUser === 'all'
                    ? 'Users'
                    : userData?.users.find((user: any) => user.id === selectedUser)?.first_name}
                </CheckUsageDropDown>
                <CheckUsageDropDown
                  options={timeOptions}
                  selectedKeys={[selectedTimeRange]}
                  onClick={(k) => {
                    setSelectedTimeRange(k);
                    if (k === 'Custom') {
                      setIsDateRangeOpen(true);
                    } else {
                      setIsCustomRangeApplied(false);
                      setStartDate('');
                      setEndDate('');
                      setIsDateRangeOpen(false);
                    }
                  }}
                  placeholder="Select Period"
                  isSearchable={false}
                />
                {isDateRangeOpen && (
                  <div className={styles.datePicker} ref={dateRef}>
                    <div className={styles.datePickerHeader}>Select Date Range</div>
                    <Input
                      type="date"
                      label="Start Date"
                      value={startDate}
                      onChange={(v) => handleStartDateChange(v)}
                      min={minDate}
                      max={maxDate}
                    />
                    <Input
                      type="date"
                      label="End Date"
                      value={endDate}
                      onChange={(v) => handleEndDateChange(v)}
                      min={minDate}
                      max={maxDate}
                    />
                    <div className={styles.datePickerButtons}>
                      <Button size="small" onClick={handleClearDate}>
                        Cancel
                      </Button>
                      <Button size="small" onClick={handleSubmitDate} disabled={!startDate || !endDate}>
                        Apply
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            {isLoading ? (
              <LoadingStatus val={6} />
            ) : (
              <div className={styles.gridContainer}>
                {renderStatCard(
                  'API Requests',
                  'Total number of API requests made',
                  formatNumberIntl(totalCountForAPI),
                  Stats_API,
                  'Explore',
                  `/usage/details/${selectedUserId}/${selectedUser}/${selectedTimeRange}`,
                  undefined,
                  true,
                  'api',
                  apiSubTypes,
                  'Total API Requests',
                  'Total number of API requests made',
                )}
                {renderStatCard(
                  'Addon Requests',
                  'Total number of addon invocations',
                  formatNumberIntl(totalCountForAddons),
                  Stats_Addons,
                  'Explore',
                  `/usage/details/${selectedUserId}/${selectedUser}/${selectedTimeRange}`,
                  undefined,
                  true,
                  'addons',
                  addonsSubTypes,
                  'Addon Requests',
                  'Total number of addon invocations',
                )}
                {renderStatCard(
                  'Transcoded Duration',
                  'Minutes spent transcoding videos',
                  totalCountForTranscodes,
                  Stats_Duration,
                  'Explore',
                  `/usage/details/${selectedUserId}/${selectedUser}/${selectedTimeRange}`,
                  convertSeconds,
                  true,
                  'transcodes',
                  [],
                  'Transcode Duration',
                  'Video transcode duration in minutes',
                )}
                {config.showDataTransferredStats && renderStatCard(
                  'Data Transferred',
                  'Data transferred from the application',
                  totalLBUsage,
                  Stats_LB,
                  'Explore',
                  `/usage/details/${selectedUserId}/${selectedUser}/${selectedTimeRange}`,
                  formatBillingBytes,
                  true,
                  'lb',
                  [],
                  'Data Transferred',
                  'Data transferred from the application',
                )}
                {isAllowedCDN &&
                  renderStatCard(
                    'CDN Usage',
                    cdnCardDescription,
                    totalCDNUsage,
                    Stats_CDN,
                    'Explore',
                    `/usage/details/${selectedUserId}/${selectedUser}/${selectedTimeRange}`,
                    formatBillingBytes,
                    true,
                    'cdn',
                    cdnSubTypes,
                    'CDN Usage',
                    cdnCardDescription,
                  )}
                {hasAiAddon && aiBudgetData?.enabled &&
                  renderStatCard(
                    'Credits',
                    'Total credits used',
                    aiCreditsUsed !== null ? formatNumberIntl(aiCreditsUsed) : null,
                    Stats_Addons,
                    'View',
                    '/insights/ai-usage',
                    undefined,
                    false,
                  )}
              </div>
            )}
            <div className={styles.chartRow}>
              <div className={styles.chartCard}>
                <ChartComponent
                  statsData={apiTopValues.length > 0 && !isCustomRangeApplied ? apiTopValues : []}
                  isLoading={statsLoading}
                  hasFetched={hasFetched}
                  graphTitle=""
                  valueInBytes={false}
                  graphType="bar"
                  title={'Top API'}
                  subtitle={''}
                  suffixXAxis={true}
                  emptyDataMsg={!isCustomRangeApplied ? 'No Data Available' : 'No API data available for Custom Date Range'}
                  table_header_name="APIs"
                />
              </div>
              <div className={styles.chartCard}>
                <ChartComponent
                  statsData={addonsTopValues.length > 0 && !isCustomRangeApplied ? addonsTopValues : []}
                  isLoading={statsLoading}
                  hasFetched={hasFetched}
                  graphTitle=""
                  valueInBytes={false}
                  graphType="bar"
                  title={'Top Addons'}
                  subtitle={''}
                  emptyDataMsg={!isCustomRangeApplied ? 'No Data Available' : 'No Addon data available for Custom Date Range'}
                  table_header_name="Addons"
                />
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default CheckUsageComponent;
